import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import PhoneInput from "@/components/PhoneInput";
import GoogleSignInButton from "@/components/GoogleSignInButton";

import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getHomeRouteForObjective } from "@/hooks/use-home-route";

import { getDefaultCountryByBrowserLanguage } from "@/constants/countries";
import { getUtmParams } from "@/lib/utm-params";
import { registerUser } from "@/lib/auth/registerUser";
import { getGaClientId } from "@/lib/analytics/gaClientId";
import { normalizeRegisterError } from "@/lib/auth/normalizeRegisterError";
import { trackEvent } from "@/lib/analytics/trackEvent";

const Register = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country: getDefaultCountryByBrowserLanguage(),
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailMarketingOptIn, setEmailMarketingOptIn] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  const navigate = useNavigate();
  const { login } = useAuth();

  // Populate form fields from query string parameters
  useEffect(() => {
    const nameParam = searchParams.get("name");
    const emailParam = searchParams.get("email");
    const phoneParam = searchParams.get("phone");

    if (nameParam || emailParam || phoneParam) {
      setFormData(prev => ({
        ...prev,
        ...(nameParam && { name: nameParam }),
        ...(emailParam && { email: emailParam }),
        ...(phoneParam && { phone: phoneParam }),
      }));
    }
  }, [searchParams]);

  const signUpStartedRef = useRef(false);

  const handleInputChange = (field: string, value: string) => {
    if (!signUpStartedRef.current) {
      signUpStartedRef.current = true;
      trackEvent("sign_up_started");
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear server error when user starts typing
    if (serverError) {
      setServerError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setServerError(t('register.errors.passwordMismatch'));
      return;
    }

    // Clear any previous server errors
    setServerError("");
    setIsLoading(true);

    try {
      // Register the user. ToS/Privacy acceptance is implied by submitting the
      // form (see the notice below the button) and stamped server-side. Job title
      // is collected post-login by the ProfileCompletionModal, not here.
      await registerUser({
        email: formData.email,
        name: formData.name,
        password: formData.password,
        phone: formData.phone,
        country: formData.country,
        locale: i18n.language === 'en' ? 'en' : 'pt',
        emailMarketingOptIn,
        utmParams: getUtmParams(),
        gaClientId: getGaClientId(),
      });

      // `sign_up_completed` is NOT fired here. It is emitted server-side from
      // the user lifecycle so that OAuth and modal signups are counted too —
      // firing it here as well would double-count email registrations.

      // Login the user after successful registration
      const user = await login(formData.email, formData.password);
      const redirectParam = searchParams.get("redirect");
      const redirectPath = redirectParam || getHomeRouteForObjective(user.onboarding_objective);
      navigate(redirectPath);
    } catch (error: unknown) {
      console.error('Registration failed:', error);

      const { code, message } = normalizeRegisterError(error);
      if (code === 'emailTaken') {
        setServerError(t('register.errors.emailTaken'));
      } else if (code === 'invalidEmail') {
        setServerError(t('register.errors.invalidEmail'));
      } else {
        setServerError(message || t('register.errors.registrationError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/images/autoclipper-logo-full.png"
              alt="Autoclipper Logo"
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white">{t('register.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {serverError && (
            <div className="mb-4 p-3 bg-red-900/20 border border-red-500 rounded-md">
              <p className="text-red-400 text-sm">{serverError}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">
                {t('common.email')}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t('register.emailPlaceholder')}
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="text-white">
                {t('register.name')}
              </Label>
              <Input
                id="name"
                type="text"
                placeholder={t('register.namePlaceholder')}
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                required
                className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
              />
            </div>

            <PhoneInput
              value={formData.phone}
              onChange={(value) => handleInputChange("phone", value)}
              country={formData.country}
              onCountryChange={(country) => handleInputChange("country", country)}
              label={t('register.phone')}
              className="text-white"
              required
            />

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">
                {t('common.password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t('register.passwordPlaceholder')}
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  required
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white">
                {t('register.confirmPassword')}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t('register.confirmPasswordPlaceholder')}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                  required
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="emailMarketingOptIn"
                checked={emailMarketingOptIn}
                onCheckedChange={(next) => setEmailMarketingOptIn(next === true)}
                className="mt-0.5 border-gray-500 data-[state=checked]:bg-lime-400 data-[state=checked]:text-black"
              />
              <Label
                htmlFor="emailMarketingOptIn"
                className="text-sm leading-snug text-gray-300 cursor-pointer font-normal"
              >
                {t('register.marketingOptIn')}
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-lime-400 to-cyan-400 hover:from-lime-500 hover:to-cyan-500 text-black font-semibold rounded-full shadow-lg"
              disabled={isLoading}
            >
              {isLoading ? t('register.submitting') : t('register.submitButton')}
            </Button>

            <p className="text-center text-xs text-gray-400">
              <Trans
                i18nKey="register.tosNotice"
                components={{
                  tos: (
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-400 hover:text-green-300 underline"
                    />
                  ),
                  privacy: (
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-400 hover:text-green-300 underline"
                    />
                  ),
                }}
              />
            </p>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-600" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-gray-800 px-2 text-gray-400">{t('common.or')}</span>
              </div>
            </div>

            <GoogleSignInButton
              redirect={searchParams.get("redirect") || "/"}
              label={t('register.signUpWithGoogle')}
            />

            <div className="text-center text-sm text-gray-400">
              {t('register.hasAccount')}{" "}
              <Link
                to={`/login${searchParams.get("redirect") ? `?redirect=${encodeURIComponent(searchParams.get("redirect")!)}` : ""}`}
                className="text-green-400 hover:text-green-300 underline"
              >
                {t('register.loginHere')}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
